import React from "react";
import { SectionCard, Field, Textarea, Input, inputCls } from "./ui";
import { useProject } from "@/context/ProjectContext";
import { uid } from "@/lib/uid";
import { getMetrics, metricGap, UNITS } from "@/lib/metrics";
import SupportingMaterials from "./SupportingMaterials";
import { toast } from "sonner";
import { TrendingDown, Plus, Trash2, Upload } from "lucide-react";

const AUDIENCE_FIELDS = [
  ["role", "Role"], ["tenure", "Tenure"], ["location", "Location"],
  ["work_environment", "Work Environment"], ["experience", "Experience & Background"], ["language", "Language Proficiency"],
];

export default function Phase1() {
  const { phaseData: pd, setPhase } = useProject();
  const p = pd.phase1 || {};
  const set = (k, v) => setPhase("phase1", { ...p, [k]: v });
  const setNested = (parent, k, v) => setPhase("phase1", { ...p, [parent]: { ...(p[parent] || {}), [k]: v } });

  const audience = p.audience || {};
  const goal = p.goal || {};

  const metrics = getMetrics(p);
  const setMetrics = (m) => setPhase("phase1", { ...p, metrics: m });
  const addMetric = () => setMetrics([...metrics, { id: uid(), name: "", current: "", target: "", unit: "%" }]);
  const updMetric = (i, k, v) => { const m = [...metrics]; m[i] = { ...m[i], [k]: v }; setMetrics(m); };
  const delMetric = (i) => setMetrics(metrics.filter((_, idx) => idx !== i));

  const stakeholders = p.stakeholders || [];
  const setStk = (s) => setPhase("phase1", { ...p, stakeholders: s });
  const addStk = () => setStk([...stakeholders, { id: uid(), name: "", role: "", contribution: "" }]);
  const updStk = (i, k, v) => { const s = [...stakeholders]; s[i] = { ...s[i], [k]: v }; setStk(s); };
  const delStk = (i) => setStk(stakeholders.filter((_, idx) => idx !== i));

  const sources = p.data_sources || [];
  const setSrc = (s) => setPhase("phase1", { ...p, data_sources: s });
  const addSrc = () => setSrc([...sources, { id: uid(), source: "", finding: "" }]);
  const updSrc = (i, k, v) => { const s = [...sources]; s[i] = { ...s[i], [k]: v }; setSrc(s); };
  const delSrc = (i) => setSrc(sources.filter((_, idx) => idx !== i));

  const tr = p.training_request || {};
  const setTr = (k, v) => setNested("training_request", k, v);

  const kickoff = p.kickoff_questions || [];
  const setKick = (s) => setPhase("phase1", { ...p, kickoff_questions: s });
  const addKick = () => setKick([...kickoff, { id: uid(), type: "", question: "" }]);
  const updKick = (i, k, v) => { const s = [...kickoff]; s[i] = { ...s[i], [k]: v }; setKick(s); };
  const delKick = (i) => setKick(kickoff.filter((_, idx) => idx !== i));

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please upload an image file (PNG, JPG, SVG, WebP)");
    if (file.size > 1.5 * 1024 * 1024) return toast.error("Logo must be under 1.5MB");
    const reader = new FileReader();
    reader.onload = () => set("company_logo", reader.result);
    reader.onerror = () => toast.error("Could not read the file");
    reader.readAsDataURL(file);
  };

  const goalPreview = `${goal.measure || "[measure]"} will ${goal.direction || "increase"} ${goal.percent ? goal.percent + "%" : "[X]%"}${goal.date ? " by " + goal.date : " by [date]"}${goal.behavior ? " as people " + goal.behavior : " as people [do something]"}`;

  return (
    <div className="space-y-6 max-w-4xl">
      <SectionCard title="Company Profile" desc="Identifies the client on the cover of the Full Client Deck." testid="phase1-company">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Company Name">
            <Input data-testid="company-name-input" value={p.company_name || ""} onChange={(e) => set("company_name", e.target.value)} placeholder="e.g. Acme Corporation" />
          </Field>
          <Field label="Business Unit">
            <Input data-testid="business-unit-input" value={p.business_unit || ""} onChange={(e) => set("business_unit", e.target.value)} placeholder="e.g. Global Sales Enablement" />
          </Field>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">Company Logo</label>
          <div className="flex items-center gap-4">
            {p.company_logo ? (
              <img src={p.company_logo} alt="Company logo" data-testid="company-logo-preview" className="h-16 w-auto max-w-[160px] object-contain border border-slate-200 rounded-md bg-white p-1" />
            ) : (
              <div className="h-16 w-28 flex items-center justify-center border border-dashed border-slate-300 rounded-md text-slate-400 text-xs">No logo</div>
            )}
            <div className="flex items-center gap-3">
              <label data-testid="upload-logo-btn" className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-md bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer">
                <Upload className="h-4 w-4" /> {p.company_logo ? "Replace" : "Upload"} Logo
                <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={onLogo} className="hidden" data-testid="logo-input" />
              </label>
              {p.company_logo && <button data-testid="remove-logo-btn" onClick={() => set("company_logo", "")} className="text-sm text-slate-400 hover:text-red-500">Remove</button>}
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-1">PNG, JPG, SVG or WebP · under 1.5MB · appears on the deck cover.</p>
        </div>
      </SectionCard>

      <SectionCard title="Executive Summary" desc="A 1–2 paragraph narrative of the key performance issue, root cause, and high-level recommendation. Appears at the top of the Needs Assessment / Analysis report." testid="phase1-exec-summary">
        <Textarea data-testid="exec-summary-input" rows={3} value={p.executive_summary || ""} onChange={(e) => set("executive_summary", e.target.value)} placeholder="Summarize the engagement at a glance…" />
      </SectionCard>

      <SectionCard title="Training Request Form" desc="Analyze the initial request before committing to a solution (Data Workbook)." testid="phase1-training-request">
        <div className="space-y-4">
          <Field label="What is the problem?" hint="Why is training being requested?">
            <Textarea data-testid="tr-problem-input" rows={2} value={tr.problem || ""} onChange={(e) => setTr("problem", e.target.value)} placeholder="The performance issue prompting this request…" />
          </Field>
          <Field label="Will learning solutions help?" hint="Is there enough information to judge feasibility?">
            <Textarea data-testid="tr-learning-help-input" rows={2} value={tr.learning_help || ""} onChange={(e) => setTr("learning_help", e.target.value)} placeholder="Whether a learning solution is likely to address it…" />
          </Field>
          <Field label="What do we still need to know?" hint="Missing details to resolve before designing.">
            <Textarea data-testid="tr-still-need-input" rows={2} value={tr.still_need || ""} onChange={(e) => setTr("still_need", e.target.value)} placeholder="Open questions for the kickoff call…" />
          </Field>
        </div>
      </SectionCard>

      <SectionCard title="Business Goals" desc="What organizational outcome must this performance support?" testid="phase1-goals">
        <Field label="Business Goal (structured)" hint="Compose a measurable goal statement.">
          <div className="grid sm:grid-cols-2 gap-2">
            <Input data-testid="goal-measure" value={goal.measure || ""} onChange={(e) => setNested("goal", "measure", e.target.value)} placeholder="Measure already used (e.g. Win rate)" />
            <select data-testid="goal-direction" value={goal.direction || "increase"} onChange={(e) => setNested("goal", "direction", e.target.value)} className={inputCls}>
              <option value="increase">increase</option>
              <option value="decrease">decrease</option>
            </select>
            <Input data-testid="goal-percent" value={goal.percent || ""} onChange={(e) => setNested("goal", "percent", e.target.value)} placeholder="By how much? (e.g. 15)" />
            <Input data-testid="goal-date" value={goal.date || ""} onChange={(e) => setNested("goal", "date", e.target.value)} placeholder="By when? (e.g. Q4 2026)" />
            <Input data-testid="goal-behavior" value={goal.behavior || ""} onChange={(e) => setNested("goal", "behavior", e.target.value)} placeholder="…as people do what? (behavior)" className="sm:col-span-2" />
          </div>
          <p data-testid="goal-preview" className="mt-2 text-sm text-slate-600 italic bg-slate-50 border border-slate-200 rounded-md px-3 py-2">{goalPreview}</p>
        </Field>
        <div className="mt-4">
          <Field label="Business Context (narrative)">
            <Textarea data-testid="business-goals-input" rows={2} value={p.business_goals || ""} onChange={(e) => set("business_goals", e.target.value)} placeholder="e.g. Increase quarterly revenue by 15% through improved sales conversion." />
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Business Goal Statement" hint="Synthesized goal statement agreed at kickoff (Data Workbook).">
            <Textarea data-testid="business-goal-statement-input" rows={2} value={p.business_goal_statement || ""} onChange={(e) => set("business_goal_statement", e.target.value)} placeholder="A single, agreed statement of the business goal…" />
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Target Audience" hint="Who must perform differently?">
            <div className="grid sm:grid-cols-2 gap-2">
              {AUDIENCE_FIELDS.map(([k, label]) => (
                <Input key={k} data-testid={`audience-${k}`} value={audience[k] || ""} onChange={(e) => setNested("audience", k, e.target.value)} placeholder={label} />
              ))}
            </div>
          </Field>
        </div>
      </SectionCard>

      <SectionCard
        title="Key Stakeholders"
        desc="People or groups involved, their role, and contribution to the engagement."
        right={<button data-testid="add-stakeholder-btn" onClick={addStk} className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800"><Plus className="h-4 w-4" /> Add</button>}
        testid="phase1-stakeholders"
      >
        <div className="space-y-2">
          {stakeholders.length === 0 && <p className="text-sm text-slate-400">No stakeholders yet.</p>}
          {stakeholders.map((s, i) => (
            <div key={s.id || i} className="grid sm:grid-cols-[1fr_1fr_1.4fr_auto] gap-2 items-center" data-testid={`stakeholder-row-${i}`}>
              <Input value={s.name} onChange={(e) => updStk(i, "name", e.target.value)} placeholder="Name / group" data-testid={`stakeholder-name-${i}`} />
              <Input value={s.role} onChange={(e) => updStk(i, "role", e.target.value)} placeholder="Role" />
              <Input value={s.contribution} onChange={(e) => updStk(i, "contribution", e.target.value)} placeholder="Contribution" />
              <button onClick={() => delStk(i)} className="text-slate-300 hover:text-red-500 justify-self-center"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </SectionCard>

      <div className="grid md:grid-cols-2 gap-6">
        <SectionCard title="Desired Performance State" testid="phase1-desired">
          <Field label="Description">
            <Textarea data-testid="desired-state-input" rows={3} value={p.desired_state || ""} onChange={(e) => set("desired_state", e.target.value)} placeholder="The optimal level of performance required." />
          </Field>
        </SectionCard>
        <SectionCard title="Current Performance State" testid="phase1-current">
          <Field label="Description">
            <Textarea data-testid="current-state-input" rows={3} value={p.current_state || ""} onChange={(e) => set("current_state", e.target.value)} placeholder="The actual performance observed today." />
          </Field>
        </SectionCard>
      </div>

      <SectionCard
        title="Quantify the Gap"
        desc="Record one or more measurable metrics with units. All metrics carry forward to Phase 6."
        right={<button data-testid="add-metric-btn" onClick={addMetric} className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800"><Plus className="h-4 w-4" /> Add Metric</button>}
        testid="phase1-metrics"
      >
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Metric Name</th>
                <th className="text-left px-3 py-2 font-semibold w-28">Current</th>
                <th className="text-left px-3 py-2 font-semibold w-28">Target</th>
                <th className="text-left px-3 py-2 font-semibold w-28">Unit</th>
                <th className="text-left px-3 py-2 font-semibold w-24">Gap</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {metrics.length === 0 && <tr><td colSpan={6} className="px-3 py-4 text-slate-400">No metrics yet. Add one to quantify the performance gap.</td></tr>}
              {metrics.map((m, i) => {
                const gap = metricGap(m);
                return (
                  <tr key={m.id || i} className="border-t border-slate-100" data-testid={`metric-row-${i}`}>
                    <td className="px-3 py-2"><input value={m.name} onChange={(e) => updMetric(i, "name", e.target.value)} placeholder="e.g. Conversion Rate" className="w-full outline-none bg-transparent" data-testid={`metric-name-${i}`} /></td>
                    <td className="px-3 py-2"><input type="number" value={m.current ?? ""} onChange={(e) => updMetric(i, "current", e.target.value)} placeholder="12" className="w-full outline-none bg-transparent" data-testid={`metric-current-${i}`} /></td>
                    <td className="px-3 py-2"><input type="number" value={m.target ?? ""} onChange={(e) => updMetric(i, "target", e.target.value)} placeholder="25" className="w-full outline-none bg-transparent" data-testid={`metric-target-${i}`} /></td>
                    <td className="px-3 py-2">
                      <select value={m.unit || ""} onChange={(e) => updMetric(i, "unit", e.target.value)} className="bg-transparent outline-none text-sm" data-testid={`metric-unit-${i}`}>
                        <option value="">unit</option>
                        {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2 font-semibold text-emerald-600">{gap === null ? "—" : gap}</td>
                    <td className="px-3 py-2"><button onClick={() => delMetric(i)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-5 rounded-lg bg-slate-900 p-5 flex items-center gap-4" data-testid="performance-gap-display">
          <div className="h-12 w-12 rounded-md bg-emerald-500/20 flex items-center justify-center">
            <TrendingDown className="h-6 w-6 text-emerald-400" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold">Tracked Metrics</p>
            <p className="font-display text-3xl font-bold text-white">{metrics.length} <span className="text-base text-slate-400 font-medium ml-1">metric{metrics.length === 1 ? "" : "s"} defined</span></p>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="Kickoff Call — Analysis Questions"
        desc="Questions to ask during the kickoff call, grouped by analysis type (Needs, Learner, Context, Job/Task)."
        right={<button data-testid="add-kickoff-btn" onClick={addKick} className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800"><Plus className="h-4 w-4" /> Add</button>}
        testid="phase1-kickoff"
      >
        <div className="space-y-2">
          {kickoff.length === 0 && <p className="text-sm text-slate-400">No kickoff questions yet.</p>}
          {kickoff.map((q, i) => (
            <div key={q.id || i} className="grid sm:grid-cols-[1fr_2fr_auto] gap-2 items-start" data-testid={`kickoff-row-${i}`}>
              <Input value={q.type} onChange={(e) => updKick(i, "type", e.target.value)} placeholder="Analysis type" data-testid={`kickoff-type-${i}`} />
              <Textarea rows={2} value={q.question} onChange={(e) => updKick(i, "question", e.target.value)} placeholder="Question(s) to ask" data-testid={`kickoff-question-${i}`} />
              <button onClick={() => delKick(i)} className="text-slate-300 hover:text-red-500 justify-self-center mt-2"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard
        title="Data Collection — Sources & Findings"
        desc="Document each data source consulted and the key findings that inform the analysis."
        right={<button data-testid="add-source-btn" onClick={addSrc} className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800"><Plus className="h-4 w-4" /> Add Source</button>}
        testid="phase1-data-collection"
      >
        <div className="space-y-2">
          {sources.length === 0 && <p className="text-sm text-slate-400">No data sources yet. Add interviews, surveys, system reports, etc.</p>}
          {sources.map((s, i) => (
            <div key={s.id || i} className="grid sm:grid-cols-[1fr_2fr_auto] gap-2 items-start" data-testid={`source-row-${i}`}>
              <Input value={s.source} onChange={(e) => updSrc(i, "source", e.target.value)} placeholder="Data source" data-testid={`source-name-${i}`} />
              <Textarea rows={2} value={s.finding} onChange={(e) => updSrc(i, "finding", e.target.value)} placeholder="Key finding(s) — facts, %, patterns" data-testid={`source-finding-${i}`} />
              <button onClick={() => delSrc(i)} className="text-slate-300 hover:text-red-500 justify-self-center mt-2"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </SectionCard>

      <SupportingMaterials phaseKey="phase1" />
    </div>
  );
}
